import type { FormControlStatus } from '@angular/forms';
import type {
    FormField,
    ReactiveFieldCondition,
    ReactiveFieldConditionRule,
    ReactiveFieldLogicContext,
} from '../../interfaces/form.interface';

interface ReactiveFieldOriginalState {
    readonly isEditable: boolean;
    readonly isVisible: boolean;
    readonly controlStatus: FormControlStatus | null;
}

export interface ReactiveFieldConditionEvaluation {
    readonly visible: boolean;
    readonly readOnly: boolean;
    readonly disabled: boolean;
}

/** Applies declarative field state rules without relying on legacy mutable callbacks. */
export class ReactiveFieldConditionEvaluator<T> {
    private readonly originalStates = new Map<string, ReactiveFieldOriginalState>();

    public hasDeclarativeConditions(field: FormField<T>): boolean {
        return field.visibleWhen !== undefined || field.readonlyWhen !== undefined || field.disabledWhen !== undefined;
    }

    public apply(field: FormField<T>, context: ReactiveFieldLogicContext<T>): ReactiveFieldConditionEvaluation | null {
        if (!this.hasDeclarativeConditions(field)) {
            return null;
        }

        const originalState = this.getOriginalState(field, context);
        const visible = this.resolveRule(field.visibleWhen, context, originalState.isVisible);
        const readOnly = this.resolveRule(field.readonlyWhen, context, false);
        const disabled = this.resolveRule(field.disabledWhen, context, false);
        const isEditable = originalState.isEditable && !readOnly && !disabled;

        field.conditions.isVisible = visible;
        field.input.isEditable = isEditable;
        this.applyControlDisabledState(field, context, disabled, originalState);

        return {
            visible,
            readOnly,
            disabled,
        };
    }

    public getRuleDependencies(rule: ReactiveFieldConditionRule<T> | undefined): readonly string[] {
        if (!this.isRuleObject(rule)) {
            return [];
        }

        return rule.dependsOn ?? [];
    }

    private getOriginalState(field: FormField<T>, context: ReactiveFieldLogicContext<T>): ReactiveFieldOriginalState {
        const key = field.name ?? '';
        const currentState = this.originalStates.get(key);

        if (currentState) {
            return currentState;
        }

        const control = context.model.get(key);
        const originalState: ReactiveFieldOriginalState = {
            isEditable: field.input.isEditable !== false,
            isVisible: field.conditions.isVisible,
            controlStatus: control?.status ?? null,
        };

        this.originalStates.set(key, originalState);

        return originalState;
    }

    private resolveRule(
        rule: ReactiveFieldConditionRule<T> | undefined,
        context: ReactiveFieldLogicContext<T>,
        fallback: boolean,
    ): boolean {
        if (typeof rule === 'boolean') {
            return rule;
        }

        if (typeof rule === 'function') {
            return rule(context);
        }

        if (this.isRuleObject(rule)) {
            return rule.resolve(context);
        }

        return fallback;
    }

    private applyControlDisabledState(
        field: FormField<T>,
        context: ReactiveFieldLogicContext<T>,
        disabled: boolean,
        originalState: ReactiveFieldOriginalState,
    ): void {
        const control = context.model.get(field.name ?? '');

        if (!control) {
            return;
        }

        if (disabled && control.enabled) {
            control.disable({ emitEvent: false });

            return;
        }

        if (!disabled && originalState.controlStatus !== 'DISABLED' && control.disabled) {
            control.enable({ emitEvent: false });
        }
    }

    private isRuleObject(
        rule: ReactiveFieldConditionRule<T> | undefined,
    ): rule is ReactiveFieldCondition<T> {
        return typeof rule === 'object' && rule !== null && typeof rule.resolve === 'function';
    }
}
